import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import JobLogDialog from './JobLogDialog';
import QueueSection from './QueueSection';
import type {
  JobLogTarget,
  QueueJob,
  QueueSectionVariant,
  QueuesResponse,
  ServerQueuesProps,
} from './types';

/** Poll interval in ms. */
const POLL_INTERVAL = 5000;

/** Rendered top to bottom, in this order. */
const SECTIONS: { variant: QueueSectionVariant; title: string }[] = [
  { variant: 'running', title: 'Running' },
  { variant: 'failed', title: 'Failed' },
  { variant: 'successful', title: 'Successful' },
  { variant: 'pending', title: 'Pending' },
];

/** Sections whose jobs have finished, and so can be ordered by when. */
const FINISHED_VARIANTS: QueueSectionVariant[] = ['failed', 'successful'];

/**
 * How long ago a job finished. Jobs with no finish time sort last rather than
 * jumping to the top - the server sends null while a job is pending or running,
 * so it means "unknown", not "just now".
 */
function finishedAge(job: QueueJob): number {
  return job.finished_seconds_ago ?? Number.POSITIVE_INFINITY;
}

/**
 * Pull one status group out of every queue and tag each job with its queue name,
 * so a section can list jobs from several queues in one table.
 *
 * Finished sections are ordered most recently finished first. The others keep
 * the order the processor sent, which for Pending is the order they will run in.
 */
function flatten(data: QueuesResponse | null, variant: QueueSectionVariant): QueueJob[] {
  if (!data) return [];

  const jobs: QueueJob[] = [];

  Object.keys(data)
    .sort()
    .forEach((queueName) => {
      const items = data[queueName]?.[variant];
      if (!Array.isArray(items)) return;
      items.forEach((job) => jobs.push({ ...job, queueName }));
    });

  if (FINISHED_VARIANTS.includes(variant)) {
    // finished_seconds_ago is an age, so smaller is more recent. Compared
    // rather than subtracted because two unknown ages are both Infinity, and
    // Infinity - Infinity is NaN, which would make the sort incoherent.
    // Sorting is stable, so jobs that finished in the same second keep their
    // queue order.
    jobs.sort((left, right) => {
      const leftAge = finishedAge(left);
      const rightAge = finishedAge(right);

      if (leftAge === rightAge) return 0;

      return leftAge < rightAge ? -1 : 1;
    });
  }

  return jobs;
}

/** Poll the queue status for one server and render it grouped by job state. */
export default function ServerQueues({ serverId, pollInterval = POLL_INTERVAL }: ServerQueuesProps) {
  const [data, setData] = useState<QueuesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // The dialog lives here rather than in JobRow because a poll can move a job
  // between sections, which unmounts its row - and the job you're reading logs
  // for is exactly the one most likely to finish while you're reading them.
  const [logTarget, setLogTarget] = useState<JobLogTarget | null>(null);

  const timerRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const mountedRef = useRef(true);

  // A poll can outlive its interval: the endpoint proxies an upstream request
  // to the processor, which is slowest exactly when a job is busy working. Two
  // guards keep that from resurrecting a job that has already finished.
  //
  // inFlightRef skips a tick while a request is outstanding, so requests never
  // pile up behind a slow one. requestRef then discards any response that is no
  // longer the newest, so an older snapshot - with the job still running -
  // cannot land after a newer one that has it successful. JobLogViewer uses the
  // same counter for the same reason.
  const inFlightRef = useRef(false);
  const requestRef = useRef(0);

  const poll = useCallback(async () => {
    if (inFlightRef.current) return;

    const request = ++requestRef.current;
    inFlightRef.current = true;

    try {
      const response = await fetch(`/admin/servers/${serverId}/queues`);

      if (!response.ok) {
        // The server sends {"error": "..."} on failure - see build_json_error_response.
        let message = `${response.status} ${response.statusText}`;
        try {
          const body = await response.json();
          if (body?.error) message = body.error;
        } catch {
          // Response body wasn't JSON - use status text
        }
        throw new Error(message);
      }

      const json: QueuesResponse = await response.json();

      if (!mountedRef.current || request !== requestRef.current) return;

      setData(json);
      setError(null);
    } catch (err) {
      if (!mountedRef.current || request !== requestRef.current) return;

      // Keep the last good data on screen and keep polling - a transient
      // failure shouldn't blank the page.
      setError(err instanceof Error ? err.message : 'Failed to get queue status.');
    } finally {
      // A superseded request leaves both flags to the newer one that replaced it.
      if (request === requestRef.current) {
        inFlightRef.current = false;

        if (mountedRef.current) {
          setLoading(false);
        }
      }
    }
  }, [serverId]);

  useEffect(() => {
    mountedRef.current = true;

    // A request left over from a previous serverId must not block the new one.
    // Its response is discarded when it lands, because requestRef has moved on.
    inFlightRef.current = false;

    // Poll immediately, then on interval
    poll();
    timerRef.current = setInterval(poll, pollInterval);

    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [poll, pollInterval]);

  const handleViewLogs = useCallback((job: QueueJob) => {
    setLogTarget({
      queueName: job.queueName,
      jobId: job.job_id,
      title: job.file_title || `File #${job.file_id}`,
    });
  }, []);

  const sections = useMemo(
    () => SECTIONS.map((section) => ({ ...section, jobs: flatten(data, section.variant) })),
    [data],
  );

  if (loading) {
    return <div className="spinner centered" />;
  }

  return (
    <div className="server-queues">
      {error && (
        <div className="error-alert" role="alert">
          {error}
        </div>
      )}

      {sections.map((section) => (
        <QueueSection
          key={section.variant}
          title={section.title}
          variant={section.variant}
          jobs={section.jobs}
          onViewLogs={handleViewLogs}
        />
      ))}

      {logTarget && (
        <JobLogDialog
          // Remount, and refetch, if the target changes without passing
          // through null.
          key={`${logTarget.queueName}:${logTarget.jobId}`}
          serverId={serverId}
          queueName={logTarget.queueName}
          jobId={logTarget.jobId}
          title={logTarget.title}
          onClose={() => setLogTarget(null)}
        />
      )}
    </div>
  );
}
